/**
 * ResearchHistoryDialog — Two-level popup for research history
 *
 * Level 1: List of all scrapeAnalyses sessions (date, goals, entity counts)
 * Level 2: Click session → opens detail dialog with ScrapeReviewPanel (reusable)
 *
 * ScrapeReviewPanel is shared between:
 * - Inline in accordion (after fresh analysis completes in review mode)
 * - Inside this history dialog (for reviewing past sessions)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Stack, Box, Typography, Button, Chip, IconButton,
  List, ListItemButton, ListItemText,
  CircularProgress, Alert, DialogContent,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import ScienceIcon from '@mui/icons-material/Science';
import { SDialog } from '@modules/ui-components/dialogs/SDialog';
import { ScrapeAnalysisService } from '@modules/firebase-core/services/ScrapeAnalysisService';
import type { ScrapeAnalysisData } from '@modules/firebase-core/services/ScrapeAnalysisService';
import { ScrapeReviewPanel } from './ScrapeReviewPanel';

// ── Status icon mapping ──────────────────────────────────────────

const STATUS_COLORS: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
  running: 'info',
  completed: 'warning',
  reviewed: 'primary',
  applied: 'success',
  error: 'error',
};

// ── Props ────────────────────────────────────────────────────────

interface ResearchHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  userId: string;
  onApplied?: () => void;
}

// ── Component ────────────────────────────────────────────────────

export function ResearchHistoryDialog({
  open,
  onClose,
  projectId,
  userId,
  onApplied,
}: ResearchHistoryDialogProps) {
  const [analyses, setAnalyses] = useState<ScrapeAnalysisData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAnalysisId, setSelectedAnalysisId] = useState<string | null>(null);

  const service = React.useMemo(() => new ScrapeAnalysisService(projectId), [projectId]);

  // Subscribe to analyses list
  useEffect(() => {
    if (!open) return;
    setIsLoading(true);
    const unsub = service.subscribe(
      (data) => { setAnalyses(data); setIsLoading(false); },
      (err) => { setError(err.message); setIsLoading(false); },
    );
    return unsub;
  }, [service, open]);

  const handleDelete = useCallback(async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try {
      await service.delete(id);
      if (selectedAnalysisId === id) setSelectedAnalysisId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  }, [service, selectedAnalysisId]);

  const formatDate = (date: unknown) => {
    try {
      if (!date) return '';
      // Firestore Timestamp — has toDate() method
      if (typeof (date as any).toDate === 'function') {
        return (date as any).toDate().toLocaleString();
      }
      // Plain object with seconds (from JSON serialization)
      if (typeof date === 'object' && (date as any).seconds) {
        return new Date((date as any).seconds * 1000).toLocaleString();
      }
      // ISO string
      if (typeof date === 'string') {
        const d = new Date(date);
        return isNaN(d.getTime()) ? date : d.toLocaleString();
      }
      return String(date);
    } catch { return ''; }
  };

  const handleApplied = useCallback(() => {
    setSelectedAnalysisId(null);
    onApplied?.();
    onClose();
  }, [onApplied, onClose]);

  // ── Level 1: Sessions list ──────────────────────────────────────

  return (
    <>
      {/* Main history dialog */}
      <SDialog
        open={open && !selectedAnalysisId}
        onClose={onClose}
        title="Research History"
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
          ) : analyses.length === 0 ? (
            <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
              <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
              <Typography color="text.secondary">No research sessions yet</Typography>
            </Stack>
          ) : (
            <List disablePadding>
              {analyses.map((analysis) => {
                const summary = analysis.summary as Record<string, unknown> | undefined;
                const entities = summary?.entitiesExtracted as Record<string, unknown> | undefined;
                const totalEntities = entities
                  ? (entities.tasksCount as number || 0) +
                    (entities.variablesCount as number || 0) +
                    (entities.reviewsCount as number || 0) +
                    (entities.ratingsCount as number || 0) +
                    (entities.bigNumbersCount as number || 0) +
                    (entities.studyCasesCount as number || 0) +
                    (entities.description ? 1 : 0)
                  : 0;

                return (
                  <ListItemButton
                    key={analysis.id}
                    onClick={() => setSelectedAnalysisId(analysis.id)}
                    sx={{
                      borderRadius: 1, mb: 0.5,
                      border: '1px solid', borderColor: 'divider',
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Typography variant="body2" fontWeight={600}>
                            {formatDate(analysis.createdAt)}
                          </Typography>
                          <Chip
                            label={analysis.status}
                            color={STATUS_COLORS[analysis.status] || 'default'}
                            size="small"
                            variant="outlined"
                          />
                          <Chip label={analysis.mode} size="small" variant="outlined" />
                          {(analysis as any).model && (
                            <Chip label={(analysis as any).model} size="small" variant="outlined" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          {/* URLs */}
                          {analysis.urls?.length > 0 && (
                            <Box sx={{ mb: 0.5 }}>
                              {analysis.urls.map((url, i) => (
                                <Typography key={i} variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 350 }}>
                                  {url}
                                </Typography>
                              ))}
                            </Box>
                          )}
                          {/* Goals + entity count */}
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {analysis.goals.map(g => (
                              <Chip key={g} label={g} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                            ))}
                            {totalEntities > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                {totalEntities} entities
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      }
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => handleDelete(e, analysis.id)}
                      title="Delete session"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </DialogContent>
      </SDialog>

      {/* Level 2: Session detail dialog — reuses ScrapeReviewPanel */}
      <SDialog
        open={!!selectedAnalysisId}
        onClose={() => setSelectedAnalysisId(null)}
        title="Research Details"
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          {selectedAnalysisId && (
            <ScrapeReviewPanel
              projectId={projectId}
              userId={userId}
              analysisId={selectedAnalysisId}
              onApplied={handleApplied}
              onClose={() => setSelectedAnalysisId(null)}
            />
          )}
        </DialogContent>
      </SDialog>
    </>
  );
}
